/**
 * TwoFactorCard — Profile > Security > 2FA enrollment UI (Vague 112).
 *
 * Flow:
 *   1. If `enabled === false`: show enable CTA → opens modal with QR + manual
 *      secret, user enters first 6-digit code, we POST /verify-setup and
 *      display the 10 backup codes (one-time reveal).
 *   2. If `enabled === true`: show "activé depuis le …", count of remaining
 *      backup codes, buttons to regenerate backups or disable 2FA.
 */
import React, { useEffect, useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Shield, ShieldCheck, Loader2, Copy, Check, Download, RefreshCw, Smartphone, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

export default function TwoFactorCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get('/auth/2fa/status');
      setStatus(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (loading) return null;
  const enabled = !!status?.enabled;

  return (
    <div className="soft-card p-5 flex flex-col h-full" data-testid="two-factor-card">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${enabled ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#EEF2FF] text-[#4F46E5]'}`}>
          {enabled ? <ShieldCheck className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Double authentification</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded ${enabled ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#F3F4F8] text-[#6B6C85]'}`} data-testid="two-factor-badge">
              {enabled ? 'Activée' : 'Non activée'}
            </span>
          </div>
        </div>
      </div>

      <p className="text-[12px] text-[#6B6C85] mb-3">
        {enabled
          ? <>Protection activée — un code à 6 chiffres est demandé à chaque connexion.</>
          : <>Renforcez la sécurité avec une application d'authentification (Google Authenticator, 1Password…).</>}
      </p>

      {enabled ? (
        <div className="space-y-3 mt-auto">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-[#EFF0F6] p-2.5 bg-[#FAFAFE]">
              <div className="text-[10px] text-[#8E8FA5] uppercase tracking-wide font-semibold">Activée depuis</div>
              <div className="text-[12px] text-[#1A1B2E] font-semibold mt-0.5 truncate">
                {status?.enabled_at ? new Date(status.enabled_at).toLocaleDateString('fr-FR') : '—'}
              </div>
            </div>
            <div className="rounded-xl border border-[#EFF0F6] p-2.5 bg-[#FAFAFE]">
              <div className="text-[10px] text-[#8E8FA5] uppercase tracking-wide font-semibold">Codes restants</div>
              <div className={`text-[12px] font-semibold mt-0.5 ${(status?.remaining_backup_codes || 0) <= 3 ? 'text-[#B91C1C]' : 'text-[#1A1B2E]'}`} data-testid="two-factor-backups-remaining">
                {status?.remaining_backup_codes ?? 0} / 10
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => setRegenOpen(true)} data-testid="two-factor-regen-btn"
                    className="h-10 px-3 rounded-xl bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#E0E7FF]">
              <RefreshCw className="w-3.5 h-3.5" /> Régénérer les codes
            </button>
            <button onClick={() => setDisableOpen(true)} data-testid="two-factor-disable-btn"
                    className="h-10 px-3 rounded-xl border border-[#FECACA] text-[#B91C1C] text-[12.5px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#FEF2F2]">
              <Shield className="w-3.5 h-3.5" /> Désactiver la 2FA
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setSetupOpen(true)} data-testid="two-factor-enable-btn"
                className="mt-auto h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:opacity-95 w-full">
          <Smartphone className="w-4 h-4" /> Activer la 2FA
        </button>
      )}

      {setupOpen && (
        <TwoFactorSetupDialog onClose={() => { setSetupOpen(false); reload(); }} />
      )}
      {disableOpen && (
        <TwoFactorDisableDialog onClose={(changed) => { setDisableOpen(false); if (changed) reload(); }} />
      )}
      {regenOpen && (
        <RegenerateBackupsDialog onClose={() => { setRegenOpen(false); reload(); }} />
      )}
    </div>
  );
}

/* ---------------- Setup Dialog (3 steps) ---------------- */
function TwoFactorSetupDialog({ onClose }) {
  const [step, setStep] = useState(1); // 1=scan QR, 2=verify, 3=backup codes
  const [payload, setPayload] = useState(null);
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [backups, setBackups] = useState([]);
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post('/auth/2fa/setup');
        setPayload(data);
        setBackups(data.backup_codes || []);
      } catch (err) {
        toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de démarrer la configuration.' });
        onClose();
      }
    })();
  }, []);

  const verify = async () => {
    setSaving(true);
    try {
      await api.post('/auth/2fa/verify-setup', { token: code.replace(/\s/g, '') });
      setStep(3);
      toast({ title: '2FA activée ✅' });
    } catch (err) {
      toast({ title: 'Code incorrect', description: err?.response?.data?.detail || 'Vérifiez votre application et réessayez.' });
    } finally { setSaving(false); }
  };

  const copySecret = async () => {
    if (!payload?.secret) return;
    try { await navigator.clipboard.writeText(payload.secret); setSecretCopied(true); setTimeout(() => setSecretCopied(false), 1500); } catch { /* noop */ }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="two-factor-setup-dialog">
        <DialogHeader>
          <DialogTitle>Activer la double authentification</DialogTitle>
          <DialogDescription>Étape {step} sur 3 — suivez le guide.</DialogDescription>
        </DialogHeader>

        {!payload ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>
        ) : step === 1 ? (
          <div className="space-y-3">
            <p className="text-[13px] text-[#4A4B63]">
              1. Installez une app d'authentification sur votre téléphone (Google Authenticator, 1Password, Authy…).
            </p>
            <p className="text-[13px] text-[#4A4B63]">2. Scannez ce QR code :</p>
            <div className="flex justify-center py-3">
              <div className="p-3 bg-white rounded-xl border border-[#EFF0F6]">
                <QRCodeSVG value={payload.otpauth_uri} size={180} includeMargin={false} />
              </div>
            </div>
            <div className="rounded-lg bg-[#FAFAFE] border border-[#EFF0F6] p-3">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-[#8E8FA5] mb-1">Ou saisissez manuellement cette clé</div>
              <div className="flex items-center justify-between gap-2">
                <code className="text-[12px] font-mono text-[#1A1B2E] break-all">{payload.secret}</code>
                <button onClick={copySecret} className="shrink-0 h-8 px-2 rounded-lg text-[#4F46E5] hover:bg-[#EEF2FF] flex items-center gap-1 text-[11.5px] font-semibold">
                  {secretCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {secretCopied ? 'Copié' : 'Copier'}
                </button>
              </div>
            </div>
            <DialogFooter>
              <button onClick={onClose} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">Annuler</button>
              <button onClick={() => setStep(2)} data-testid="two-factor-next-btn"
                      className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">
                Continuer
              </button>
            </DialogFooter>
          </div>
        ) : step === 2 ? (
          <div className="space-y-3">
            <p className="text-[13px] text-[#4A4B63]">Entrez le code à 6 chiffres affiché dans votre app :</p>
            <input
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              data-testid="two-factor-verify-input"
              className="w-full h-14 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4F46E5] px-4 text-center tracking-[0.5em] text-[24px] font-bold font-mono"
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
            />
            <p className="text-[11px] text-[#8E8FA5]">Si le code est refusé, vérifiez que l'heure de votre téléphone est synchronisée.</p>
            <DialogFooter>
              <button onClick={() => setStep(1)} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">Retour</button>
              <button onClick={verify} disabled={code.length !== 6 || saving} data-testid="two-factor-verify-btn"
                      className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Vérifier et activer
              </button>
            </DialogFooter>
          </div>
        ) : (
          <BackupCodesView backups={backups} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Backup codes view (reused for regenerate) ---------------- */
function BackupCodesView({ backups, onClose }) {
  const txtRef = useRef(null);
  const download = () => {
    const blob = new Blob([
      "Codes de secours OFManager — 2FA\n",
      "Chaque code ne peut être utilisé qu'une seule fois. Conservez-les en lieu sûr.\n\n",
      backups.join("\n"),
    ], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'ofmanager-2fa-backup-codes.txt';
    a.click(); URL.revokeObjectURL(url);
  };
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(backups.join('\n')); toast({ title: 'Codes copiés' }); } catch { /* noop */ }
  };
  return (
    <div className="space-y-3" data-testid="two-factor-backups-view">
      <div className="rounded-xl bg-[#FEF3C7] border border-[#FCD34D] p-3 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-[#92400E] shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-[#92400E] leading-snug">
          <strong>Sauvegardez ces 10 codes maintenant.</strong> Ils servent à vous reconnecter si vous perdez accès à votre téléphone. Ils ne seront plus affichés après cette fenêtre.
        </div>
      </div>
      <div ref={txtRef} className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-4 grid grid-cols-2 gap-2 font-mono text-[13px] text-[#1A1B2E]">
        {backups.map((c) => <div key={c} className="text-center py-1">{c}</div>)}
      </div>
      <div className="flex gap-2">
        <button onClick={copyAll} className="flex-1 h-10 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold flex items-center justify-center gap-2">
          <Copy className="w-4 h-4" /> Copier
        </button>
        <button onClick={download} className="flex-1 h-10 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold flex items-center justify-center gap-2">
          <Download className="w-4 h-4" /> Télécharger
        </button>
      </div>
      <DialogFooter>
        <button onClick={onClose} data-testid="two-factor-backups-done"
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">
          J'ai sauvegardé mes codes
        </button>
      </DialogFooter>
    </div>
  );
}

/* ---------------- Disable dialog ---------------- */
function TwoFactorDisableDialog({ onClose }) {
  const [pwd, setPwd] = useState('');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/auth/2fa/disable', { password: pwd || undefined, token: token || undefined });
      toast({ title: '2FA désactivée' });
      onClose(true);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Désactiver la 2FA</DialogTitle>
          <DialogDescription>Confirmez avec votre mot de passe ou un code 2FA.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Mot de passe"
                 className="w-full h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px]" data-testid="two-factor-disable-password" />
          <div className="text-center text-[11px] text-[#8E8FA5]">ou</div>
          <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Code 2FA (6 chiffres) ou code de secours"
                 className="w-full h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px] font-mono" data-testid="two-factor-disable-token" />
        </div>
        <DialogFooter>
          <button onClick={() => onClose(false)} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">Annuler</button>
          <button onClick={submit} disabled={saving || (!pwd && !token)} data-testid="two-factor-disable-confirm"
                  className="h-10 px-4 rounded-xl bg-[#DC2626] text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Désactiver
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Regenerate backup codes ---------------- */
function RegenerateBackupsDialog({ onClose }) {
  const [backups, setBackups] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post('/auth/2fa/regenerate-backups');
        setBackups(data.backup_codes || []);
      } catch (err) {
        toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
        onClose();
      }
    })();
  }, []);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveaux codes de secours</DialogTitle>
          <DialogDescription>Vos anciens codes ne sont plus valides. Sauvegardez ceux-ci.</DialogDescription>
        </DialogHeader>
        {backups ? <BackupCodesView backups={backups} onClose={onClose} /> : (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>
        )}
      </DialogContent>
    </Dialog>
  );
}
